import { Link } from 'react-router-dom';
import { 
  Building2, 
  Briefcase, 
  Users, 
  Award, 
  Shield, 
  MapPin, 
  ChevronRight,
  CheckCircle2,
  FlaskConical,
  HardHat,
  GraduationCap,
  Handshake,
  Globe,
  Clock,
  Target
} from 'lucide-react';

export const Home = () => {
  const highlights = [
    {
      icon: Building2,
      title: 'Construction & Engineering',
      description: 'Large-scale construction and civil engineering projects across India.',
    },
    {
      icon: Briefcase,
      title: 'Trading & Imports',
      description: 'Authorised dealing of specialised industrial chemicals including PET and Resin.',
    },
    {
      icon: Users,
      title: 'Vocational Training',
      description: 'Skill development and manpower consulting for industry and government programs.',
    },
  ];

  const whyChooseUs = [
    {
      icon: Clock,
      title: 'Legacy Since 1983',
      description: 'Four decades of trusted service',
    },
    {
      icon: Handshake,
      title: 'Govt. & Corporate',
      description: 'Trusted by public & private sectors',
    },
    {
      icon: Globe,
      title: 'Pan India Reach',
      description: 'Operations across the nation',
    },
    {
      icon: Target,
      title: 'Turnkey Delivery',
      description: 'End-to-end project solutions',
    },
  ];

  const experienceHighlights = [
    { icon: Building2, text: 'Infrastructure & Civil Projects' },
    { icon: FlaskConical, text: 'Chemical Trading & Imports' },
    { icon: GraduationCap, text: 'Skill Development Programs' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1633363961301-4f100a78e92d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxicmlkZ2UlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzc1NjQ1NzAwfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-3xl">
            <p className="overline mb-4 animate-fade-in-up">Established 1983</p>
            <h1 
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in-up animation-delay-200"
            >
              Building India's Future<br />
              <span className="text-[#C9A84C]">With Excellence</span>
            </h1>
            <p className="text-lg text-[#F5F5F0]/90 mb-8 max-w-xl animate-fade-in-up animation-delay-400">
              Four decades of engineering excellence in executing, consulting, and delivering 
              turnkey projects for Government bodies and Corporate clients across India.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-600">
              <Link 
                to="/about" 
                data-testid="hero-cta-primary"
                className="btn-gold inline-flex items-center gap-2"
              >
                Know More
                <ChevronRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/contact" 
                data-testid="hero-cta-secondary"
                className="bg-transparent border-2 border-white text-white font-semibold px-6 py-3 rounded-sm hover:bg-white hover:text-[#1A2B4A] transition-all inline-flex items-center gap-2"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section 
        data-testid="stats-banner"
        className="bg-[#1A2B4A] py-4"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 text-center">
            <p className="text-[#F5F5F0]/90 font-medium">
              <span className="text-[#C9A84C] font-bold">40+ Years</span> | Pan India Presence | Government & Corporate Projects
            </p>
          </div>
        </div>
      </section>

      {/* Highlight Cards Section */}
      <section 
        data-testid="highlights-section"
        className="py-20 md:py-32 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-4">What We Do</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2B4A] mb-4">
              Our Core Competencies
            </h2>
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Delivering excellence across multiple sectors with integrated solutions 
              that drive India's infrastructure growth.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <div
                key={index}
                data-testid={`highlight-card-${index}`}
                className="service-card bg-white p-8 border border-[#4A5568]/15 rounded-sm"
              >
                <div className="icon-container mb-6">
                  <item.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#1A2B4A] mb-3">{item.title}</h3>
                <p className="text-[#4A5568] leading-relaxed">{item.description}</p>
                <Link 
                  to="/services" 
                  className="inline-flex items-center gap-2 mt-6 text-[#C9A84C] font-semibold hover:gap-3 transition-all"
                >
                  Learn More <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - Business Verticals */}
      <section 
        data-testid="stats-section"
        className="py-12 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-[#C9A84C] text-xs font-bold uppercase tracking-[0.2em] mb-8">Our Business Verticals</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {experienceHighlights.map((item, index) => (
              <div 
                key={index} 
                data-testid={`experience-item-${index}`}
                className="flex flex-col items-center text-center group"
              >
                <div className="w-16 h-16 rounded-full bg-[#C9A84C]/10 border-2 border-[#C9A84C]/30 flex items-center justify-center mb-4 group-hover:bg-[#C9A84C]/20 group-hover:border-[#C9A84C] transition-all duration-300">
                  <item.icon className="w-7 h-7 text-[#C9A84C]" />
                </div>
                <span className="text-[#F5F5F0] font-medium text-sm md:text-base">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        data-testid="why-choose-us-section"
        className="py-20 md:py-32 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-4">Why Partner With Us</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2B4A] mb-4">
              The Kamal Engineers Advantage
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                data-testid={`why-choose-card-${index}`}
                className="bg-white p-6 border border-[#4A5568]/15 rounded-sm text-center hover:border-[#C9A84C] transition-colors duration-300"
              >
                <div className="w-14 h-14 rounded-full bg-[#1A2B4A] flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#C9A84C]" />
                </div>
                <h3 className="text-lg font-bold text-[#1A2B4A] mb-2">{item.title}</h3>
                <p className="text-[#4A5568] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="cta-section"
        className="py-20 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-[#F5F5F0]/80 mb-8 max-w-2xl mx-auto">
            Get in touch with our team to discuss your infrastructure and engineering needs.
          </p>
          <Link 
            to="/contact" 
            data-testid="cta-contact-button"
            className="btn-gold inline-flex items-center gap-2"
          >
            Contact Us Today
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
